import { MetrixCourseDetailService } from './metrixCourseDetailService';

type CacheEntry = {
  value: number;
  timestamp: number;
};

export class CourseHoleService {
  private static instance: CourseHoleService;
  private cache: Record<string, CacheEntry> = {};
  private pendingRequests: Record<string, Promise<number | null>> = {};
  private CACHE_TTL = 24 * 60 * 60 * 1000; // 24h välimuisti

  private constructor() {
    this.loadCache();
  }

  public static getInstance(): CourseHoleService {
    if (!CourseHoleService.instance) {
      CourseHoleService.instance = new CourseHoleService();
    }
    return CourseHoleService.instance;
  }

  private loadCache() {
    const savedData = sessionStorage.getItem('courseHoleCache');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData) as Record<string, CacheEntry>;
        // Poista vanhentuneet välimuistitiedot
        Object.keys(parsed).forEach(key => {
          if (Date.now() - parsed[key].timestamp < this.CACHE_TTL) {
            this.cache[key] = parsed[key];
          }
        });
      } catch (e) {
        console.error('Failed to parse hole cache', e);
      }
    }
  }

  // Muutetaan julkiseksi
  public saveCache() {
    sessionStorage.setItem('courseHoleCache', JSON.stringify(this.cache));
  }

  public async getHoleCount(courseId: string, courseType?: string): Promise<number | null> {
    // Skip API call for parent courses (Type 1) - they should have hole data in Firestore
    // This check is redundant since we already check in the hook, but keeping as safeguard
    if (courseType === '1') {
      return null; // Parent course data should be handled by the hook
    }
    
    // Tarkista välimuisti ja sen päiväys
    if (this.cache[courseId] && 
        Date.now() - this.cache[courseId].timestamp < this.CACHE_TTL) {
      return this.cache[courseId].value;
    }

    // Tarkista onko pyyntö jo käynnissä
    const existingRequest = this.pendingRequests[courseId];
    if (existingRequest !== undefined) {
      return existingRequest;
    }

    // Only make API call for layout courses (Type 2) or unknown types

    const fetchPromise = MetrixCourseDetailService.getInstance()
      .fetchCourseHoleCount(courseId)
      .then(count => {
        if (count !== null) {
          this.cache[courseId] = {
            value: count,
            timestamp: Date.now()
          };
          this.saveCache();
        }
        delete this.pendingRequests[courseId];
        return count;
      })
      .catch(err => {
        console.error(`Failed to fetch holes for ${courseId}`, err);
        // Yritä uudelleen 30s kuluttua
        setTimeout(() => {
          delete this.pendingRequests[courseId];
        }, 30000);
        return null;
      });

    this.pendingRequests[courseId] = fetchPromise;
    return fetchPromise;
  }

  // Pakota päivitys tietyn radan tiedoille
  public async refreshHoleCount(courseId: string): Promise<number | null> {
    delete this.cache[courseId];
    return this.getHoleCount(courseId);
  }
}
